/**
 * ResetPasswordForm — autenticazione Phase 6 (IMPL-P6-002).
 *
 * UC-008 step 2 form. `react-hook-form` + `zodResolver(resetPasswordSchema)`
 * from `@app/shared`. The token comes from the parent page (read off the
 * `?token=` query param) and is held in form state as a hidden field so the
 * shared schema's `.refine` cross-field check (password === confirmPassword)
 * still runs.
 *
 * Server-error handling:
 *  - 400 INVALID_TOKEN  → Alert "Il link non è valido"
 *  - 400 TOKEN_EXPIRED  → Alert "Il link è scaduto"
 *  - any other          → generic neutral copy via `serverErrorToItalian`
 *
 * On success: `navigate('/reimposta-password/successo')`.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from 'react-router-dom';
import { resetPasswordSchema, type ResetPasswordInput } from '@app/shared';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

import { authClient, isAuthClientError } from '../lib/auth-client';
import { serverErrorToItalian } from '../lib/errors';
import { PasswordField } from './PasswordField';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';

interface ServerError {
  status: number;
  code: string;
}

interface ResetPasswordFormProps {
  token: string;
}

export function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const [serverError, setServerError] = useState<ServerError | null>(null);
  const navigate = useNavigate();

  const form = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onTouched',
    defaultValues: {
      token,
      password: '',
      confirmPassword: '',
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const passwordValue = watch('password') ?? '';

  async function onSubmit(values: ResetPasswordInput) {
    setServerError(null);
    try {
      await authClient.resetPassword(values);
      navigate('/reimposta-password/successo', { replace: true });
    } catch (err: unknown) {
      if (isAuthClientError(err)) {
        setServerError({ status: err.status, code: err.code });
      } else {
        setServerError({ status: 0, code: 'UNKNOWN' });
      }
    }
  }

  const isInvalidToken =
    serverError?.code === 'INVALID_TOKEN' || serverError?.code === 'TOKEN_EXPIRED';

  return (
    <form
      noValidate
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-5"
      aria-describedby={serverError ? 'reset-server-error' : undefined}
    >
      {/* Token (hidden) — kept in form state so resetPasswordSchema's
          cross-field refine can still run on the client. */}
      <input type="hidden" {...register('token')} />

      {/* New password + strength meter */}
      <div className="space-y-2">
        <Label htmlFor="reset-password">Nuova password</Label>
        <PasswordField
          id="reset-password"
          autoComplete="new-password"
          aria-describedby="reset-password-strength reset-password-error"
          invalid={!!errors.password}
          {...register('password')}
        />
        <PasswordStrengthMeter id="reset-password-strength" password={passwordValue} />
        {errors.password ? (
          <p id="reset-password-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.password.message}
          </p>
        ) : null}
      </div>

      {/* Confirm password */}
      <div className="space-y-2">
        <Label htmlFor="reset-confirm">Conferma nuova password</Label>
        <PasswordField
          id="reset-confirm"
          autoComplete="new-password"
          aria-describedby={errors.confirmPassword ? 'reset-confirm-error' : undefined}
          invalid={!!errors.confirmPassword}
          {...register('confirmPassword')}
        />
        {errors.confirmPassword ? (
          <p id="reset-confirm-error" role="alert" className="text-sm font-medium text-destructive">
            {errors.confirmPassword.message}
          </p>
        ) : null}
      </div>

      {/* Server-side errors */}
      {serverError ? (
        isInvalidToken ? (
          <Alert variant="destructive" id="reset-server-error">
            <AlertTitle>
              {serverError.code === 'TOKEN_EXPIRED' ? 'Il link è scaduto' : 'Il link non è valido'}
            </AlertTitle>
            <AlertDescription>
              {serverError.code === 'TOKEN_EXPIRED'
                ? 'Il link di reimpostazione è scaduto.'
                : 'Il link di reimpostazione non è valido.'}{' '}
              <Link
                to="/password-dimenticata"
                className="underline underline-offset-4 font-medium"
              >
                Richiedi un nuovo link
              </Link>
              .
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive" id="reset-server-error">
            <AlertDescription>{serverErrorToItalian(serverError.code)}</AlertDescription>
          </Alert>
        )
      ) : null}

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Aggiornamento in corso…' : 'Aggiorna password'}
      </Button>
    </form>
  );
}
